import { SearchService } from '@stockcare-services/search-service/search.service';
import { filter, flatMapDeep } from 'lodash';
import { Component, OnInit, Input, EventEmitter, Output, ViewChild, OnDestroy } from '@angular/core';
import { StockService } from '@stockcare-services/stock-service/stock.service';
import { StockStorageService } from '@stockcare-storage/stock/stock-storage.service';
import { Stock } from '@stockcare-storage/stock/stock';
import { Subscription } from 'rxjs';
import { IStockLevel } from '@stockcare-models/IStockLevel';
import { BasketStagingStorageService } from '@stockcare-storage/basket-staging/basket-staging-storage.service';
import { MaterialType } from '@stockcare-models/MaterialType';
import { ServicesStorageService } from '@stockcare-storage/service/service-storage.service';
import { Service } from '@stockcare-storage/service/service';
import { ApplicationInsightsService } from '@stockcare-services/application-insights/application-insights.service';
import { Settings } from '@stockcare-helpers/settings';
import { SettingsService } from '@stockcare-services/settings-service/settings.service';
import { PackService } from '@stockcare-services/pack-service/pack.service';
import { IPack } from '@stockcare-models/IPack';
import { DeviceService } from '@stockcare-services/devices-service/devices.service';
import { MatDialog } from '@angular/material/dialog';
import { TheatreBasketPack } from '@stockcare-models/TheatreBasketPack';
import { ServicesService } from '@stockcare-services/service/services.service';
import { NotificationService } from '@stockcare-services/notification-service/notification.service';
import { PackStorageService } from '@stockcare-storage/pack/pack-storage.service';
import { NoStockMaterialItem } from '@stockcare-models/NoStockMaterialItem';
import { TheatreAddPackToBasketDialog } from '@stockcare-components/theatre-add-pack-to-basket-dialog/theatre-add-pack-to-basket-dialog.component';

@Component({
  standalone: false,
  selector: 'app-materials-list',
  templateUrl: './materials-list.component.html',
  styleUrls: ['./materials-list.component.scss'],
})
export class MaterialsListComponent implements OnInit, OnDestroy {
  @Output() stockClicked = new EventEmitter<Stock>();
  @Output() serviceClicked = new EventEmitter<Service>();
  @Output() packClicked = new EventEmitter<TheatreBasketPack>();
  @Output() basketCancelClicked = new EventEmitter<boolean>();
  @Output() materialsLoaded = new EventEmitter<boolean>();
  @Output() searchHeaderInputFocused: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() public predictiveTextWords: EventEmitter<string[]> = new EventEmitter<string[]>();
  @Input() isSmall: boolean = false;
  @Input() hideSearch: boolean = false;
  @Input() header: string = 'BROWSE AVAILABLE MATERIALS';
  @Input() searchInput: string;
  @Input() placeHolderText: string = 'Scan Materials';
  @Input() disableKeyPress: boolean;
  @Input() showMaterialTypeSelector: boolean;
  @Input() showSearch: boolean = true;
  @Input() hidePacks: boolean = false;
  @Input() hideHeader: boolean = false;
  @Input() showRefresh: boolean = true;
  @Input() selectedFilter: MaterialType = MaterialType.Materials;
  @Input() useTheatreCards: boolean = false;
  @Input() isBasketScanningEnabled: boolean = false;
  @Input() hideServices: boolean = false;
  @Input() patientId: number = 0;
  @Input() enableSearch: boolean = true;
  @Input() isFromTheatreDefaultConfig: boolean = false;

  groupedMaterials: any;
  stockList = {};
  offLineStocks: Stock[];
  allOffLineStocks: Stock[];
  filteredStocks: Stock[] = [];
  offlinePacks: IPack[] = [];
  allOfflinePacks: IPack[] = [];
  filteredPacks: IPack[] = [];
  searchVar: string;
  groupedServices: any;
  servicesList: Service[] = [];
  offLineServices: Service[];
  allOffLineServices: Service[];
  filteredServices: Service[] = [];
  packsEnabled: boolean = false;
  subscriptions = new Subscription();
  addPackDialogOpen: boolean;
  enableTrackingForNoStockItems: boolean;
  suggestedWords: string[] = [];
  isTheatreBackOfficeDevice: boolean = false;

  constructor(
    private readonly stockService: StockService,
    private readonly packService: PackService,
    private readonly stockStorageService: StockStorageService,
    private readonly servicesStorageService: ServicesStorageService,
    private readonly searchService: SearchService,
    private readonly basketStagedStorageService: BasketStagingStorageService,
    private readonly applicationInsights: ApplicationInsightsService,
    private readonly settingService: SettingsService,
    private readonly deviceService: DeviceService,
    private readonly dialog: MatDialog,
    private readonly servicesService: ServicesService,
    private readonly notify: NotificationService,
    private readonly packStorageService: PackStorageService
  ) { }

  async ngOnInit() {
    this.getMaterials();
    if (this.enableSearch) {
      this.search();
    }
    this.buildPacksEnabled();
    if (this.deviceService.isTheaterDevice) {
      this.getServices();
    }
    this.isTheatreBackOfficeDevice = this.deviceService.isTheatreBackOfficeDevice();
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  buildPacksEnabled() {
    var packsEnabledForTheatre = this.settingService.getBooleanSetting(Settings.THEATRE_PACKS_ENABLED);
    var packsEnabledForWardBilling = this.settingService.getBooleanSetting(Settings.ENABLE_PACKS_FOR_NORMAL_WARD_BILLING);
    this.enableTrackingForNoStockItems = this.settingService.getBooleanSetting(
      Settings.ENABLE_EMAILS_FOR_MATERIALS_WITH_NO_STOCK
    );

    if (this.deviceService.isTheaterDevice() && packsEnabledForTheatre) {
      this.packsEnabled = true;
      return;
    }

    if (!this.deviceService.isTheaterDevice() && packsEnabledForWardBilling) {
      this.packsEnabled = true;
      return;
    }

    this.packsEnabled = false;
    return;
  }

  async getMaterials() {
    this.stockService.getMaterials(this.deviceService.getStockLocationId()).subscribe(
      async (stocks: Stock[]) => {
        await this.buildStocks(stocks);
      },
      async (error) => {
        this.applicationInsights.logEvent('get-materials', {
          success: false,
          error: error,
        });
        await this.buildStocks([]);
      }
    );
  }

  private async buildStocks(stocks: Stock[]) {
    if (stocks === null) {
      return;
    }

    if (stocks.length === 0) {
      this.offLineStocks = await this.stockStorageService.getStocks();
      this.allOffLineStocks = this.offLineStocks;
      stocks = this.offLineStocks;
    } else {
      try {
        this.allOffLineStocks = stocks;

        this.stockStorageService.deleteAll().then(() => {
          this.stockStorageService.addStocks(stocks);
        });
      } catch (error) {
        this.applicationInsights.logEvent('get-materials', {
          success: false,
          error: error,
        });
      }
    }
    await this.adjustQuantitiesForStagedStock(stocks);
    this.offLineStocks = stocks.slice(0, 20);
    this.filterMaterials('');
    this.buildWordsForPredictiveText(stocks)
    this.materialsLoaded.emit(true);
  }

  buildWordsForPredictiveText(stocks: IStockLevel[]) {
     var barcodes = stocks.map(stock => stock.barcode);
     var names = stocks.map(stock => stock.name);

     this.suggestedWords.push(...barcodes);
     this.suggestedWords.push(...names);

     this.predictiveTextWords.emit(this.suggestedWords);
  }

  async getServices() {
    if (this.allOffLineServices && this.allOffLineServices.length > 0) {
      return;
    }

    this.servicesService.getAll(this.deviceService.getInstitutionId(), this.deviceService.getStockLocationId()).subscribe(
      () => {
        this.servicesStorageService.getServices().then((result) => {
          this.offLineServices = result.slice(0, 20);
          this.allOffLineServices = result;
          this.filterServices('');
        });
      },
      (error) => {
        this.servicesStorageService.getServices().then((result) => {
          this.offLineServices = result.slice(0, 20);
          this.allOffLineServices = result;
          this.filterServices('');
        });
      }
    );
  }

  async getPacks() {
    if (this.allOfflinePacks && this.allOfflinePacks.length > 0) {
      return;
    }

    this.packService.getPacks(this.deviceService.getInstitutionId(), this.deviceService.getStockLocationId()).subscribe(
      (result) => {
        result?.forEach((pack) => {
          pack.materials.forEach((material) => {
            const stock = this.allOffLineStocks.find((x) => x.barcode === material.barcode);
            material.availableQuantity = stock?.quantity;
          });
        });
        this.offlinePacks = result.slice(0, 20);
        this.allOfflinePacks = result;
        this.filteredPacks = this.offlinePacks;
      },
      () => {
        this.packStorageService.getPacks().then((result) => {
          var packs = this.packService.convertPackToIPack(result);
          this.allOfflinePacks = packs;
          this.filteredPacks = this.offlinePacks;
        });
      }
    );
  }

  async adjustQuantitiesForStagedStock(stocks: IStockLevel[]) {
    const stagedStocks = await this.getStagedBaskets();

    const groupedStagedBaskets = stagedStocks.reduce((group, item) => {
      let barcodeItem = group.find((x) => x.barcode === item.barcode);

      if (barcodeItem) {
        barcodeItem.quantity += item.quantity;
      } else {
        group.push(item);
      }

      return group;
    }, []);

    for (const groupedStagedBasket of groupedStagedBaskets) {
      var stockItem = stocks.find((x) => x.barcode === groupedStagedBasket);

      if (stockItem) {
        stockItem.quantity -= groupedStagedBasket.quantity;
      }
    }
  }

  async getStagedBaskets() {
    const stagedStocks = [];
    const dbStagedBaskets = await this.basketStagedStorageService.getStagedBaskets();

    const unprocessedBaskets = filter(dbStagedBaskets, (b) => {
      return b.Processed === false;
    });

    const patientStocks = flatMapDeep(unprocessedBaskets, (b) => JSON.parse(b.PatientStocksJson));

    for (const patientStock of patientStocks) {
      stagedStocks.push({
        quantity: patientStock.quantity,
        barcode: patientStock.barcode,
      });
    }

    return stagedStocks;
  }

  async getOfflineMaterials(): Promise<Stock[]> {
    return await this.stockStorageService.getStocks();
  }

  search() {
    this.subscriptions.add(
      this.searchService.scanned().subscribe((searchValue) => {
        if (this.selectedFilter === 'Materials') {
          this.filterMaterialsScanned(searchValue);
        }

        if (this.deviceService.isTheaterDevice() && this.selectedFilter === 'Services') {
          this.filterServicesScanned(searchValue);
        }

        if (this.deviceService.isTheaterDevice() && this.selectedFilter === 'Packs') {
          this.filterPacksScanned(searchValue);
        }
      })
    );

    this.subscriptions.add(
      this.searchService.typed().subscribe((searchValue) => {
        if (this.selectedFilter === 'Materials') {
          this.filterMaterials(searchValue);
        }

        if (this.deviceService.isTheaterDevice() && this.selectedFilter === 'Services') {
          this.filterServices(searchValue);
        }

        if (this.deviceService.isTheaterDevice() && this.selectedFilter === 'Packs') {
          this.filterPacks(searchValue);
        }
      })
    );
  }

  private filterMaterialsScanned(searchValue: string) {
    if (!this.showSearch) {
      return;
    }

    if (!searchValue) {
      this.filteredStocks = this.offLineStocks.slice(0, 20);
      return;
    }

    var filteredMaterial = this.allOffLineStocks.find((x) => x.barcode.toLowerCase() === searchValue.toLowerCase());

    if (filteredMaterial != null) {
      this.addStock(filteredMaterial);
      return;
    } else {
      this.searchService.clear();
      this.notify.error('No matching material was found', 'Material not found');
    }
  }

  private filterMaterials(searchValue: string) {
    if (this.showSearch) {
      if (!searchValue) {
        this.filteredStocks = this.offLineStocks.slice(0, 20);
        return;
      }

      if (searchValue.length < 3) {
        return;
      }

      this.filteredStocks = this.allOffLineStocks.filter(
        (x) =>
          x.barcode.toLowerCase().indexOf(searchValue.toLowerCase()) > -1 || x.name.toLowerCase().indexOf(searchValue.toLowerCase()) > -1
      );
    }
  }

  private filterPacks(searchValue: string) {
    if (this.showSearch) {
      if (!searchValue) {
        this.filteredPacks = this.offlinePacks.slice(0, 20);
        return;
      }

      this.filteredPacks = this.allOfflinePacks
        .filter(
          (x) =>
            x.barcode.toLowerCase().indexOf(searchValue.toLowerCase()) > -1 || x.name.toLowerCase().indexOf(searchValue.toLowerCase()) > -1
        )
        .slice(0, 20);
    }
  }

  private filterServicesScanned(searchValue: string) {
    if (!this.showSearch) {
      return;
    }

    if (!searchValue) {
      this.filteredServices = this.offLineServices.slice(0, 20);
      return;
    }

    const parsedSearchString = parseInt(searchValue);

    var filteredService = null;

    if (isNaN(parsedSearchString)) {
      filteredService = this.allOffLineServices.find((x) => x.Barcode.toLowerCase() === searchValue.toLowerCase());
    } else {
      filteredService = this.allOffLineServices.find((x) => parseInt(x.Barcode) === parsedSearchString);
    }

    if (filteredService != null) {
      this.addService(filteredService);
      return;
    } else {
      this.searchService.clear();
      this.notify.error('No matching service was found', 'Service not found');
    }
  }

  private filterPacksScanned(searchValue: string) {
    if (!this.showSearch) {
      return;
    }

    if (!searchValue) {
      this.filteredPacks = this.offlinePacks.slice(0, 20);
      return;
    }

    var filteredPack = this.allOfflinePacks.find((x) => x.barcode.toLowerCase() === searchValue.toLowerCase());

    if (filteredPack != null) {
      this.packCardClicked(filteredPack);
      return;
    } else {
      this.searchService.clear();
      this.notify.error('No matching pack was found', 'Pack not found');
    }
  }

  private filterServices(searchValue: string) {
    if (this.showSearch) {
      if (!searchValue) {
        this.filteredServices = this.offLineServices.slice(0, 20);
        return;
      }

      if (searchValue.length < 3) return;

      this.filteredServices = this.allOffLineServices
        .filter(
          (x) =>
            x.Barcode.toLowerCase().indexOf(searchValue.toLowerCase()) > -1 || x.Name.toLowerCase().indexOf(searchValue.toLowerCase()) > -1
        )
        .slice(0, 20);
    }
  }

  private addStock(stock: Stock) {
    if (this.isBasketScanningEnabled) {
      return;
    }

    if (stock.quantity > 0) {
      this.stockClicked.emit(stock);
    } else {
      this.submitNoStockFoundForMaterial(stock);
      this.stockClicked.emit(null);
    }
    this.searchService.clear();
    return;
  }

  private addService(service: Service) {
    if (this.isBasketScanningEnabled) {
      return;
    }

    this.serviceClicked.next(service);
    this.searchService.clear();
    return;
  }

  cardClicked(stock: Stock) {
    if (this.isTheatreBackOfficeDevice && this.isFromTheatreDefaultConfig == false) {
      this.notify.error('Edits cannot be made using Theatre Back Office Devices');
      return;
    }

    if (stock.quantity > 0) {
      this.stockClicked.emit(stock);
    } else {
      this.submitNoStockFoundForMaterial(stock);
      this.stockClicked.emit(null);
    }
  }

  serviceCardClicked(service: Service) {
    if (this.isTheatreBackOfficeDevice && this.isFromTheatreDefaultConfig == false) {
      this.notify.error('Edits cannot be made using Theatre Back Office Devices');
      return;
    }

    this.serviceClicked.next(service);
  }

  disableBasketScanningClicked() {
    this.basketCancelClicked.emit();
  }

  async packCardClicked(pack: IPack) {
    if (this.isTheatreBackOfficeDevice && this.isFromTheatreDefaultConfig == false) {
      this.notify.error('Edits cannot be made using Theatre Back Office Devices');
    } else {
      if (this.addPackDialogOpen === true) return;

      if (this.isBasketScanningEnabled) {
        return;
      }

      this.addPackDialogOpen = true;
      const theatrePack = await this.packService.buildBasketPack(pack);
      if (theatrePack.hasUnavailableItems) {
        const addToBasketDialog = this.dialog.open(TheatreAddPackToBasketDialog, {
          width: '500px',
          data: theatrePack,
        });
        addToBasketDialog.afterClosed().subscribe(async (packAddedToBasket) => {
          this.addPackDialogOpen = false;
          if (packAddedToBasket) {
            this.packClicked.next(packAddedToBasket);
          }
        });
      } else {
        this.packClicked.next(theatrePack);
        this.addPackDialogOpen = false;
      }
    }
  }

  onFilterChange(selectedFilter) {
    this.selectedFilter = selectedFilter.value;

    if (!this.deviceService.isTheaterDevice) {
      return;
    }

    if (selectedFilter.value === 'Packs' && this.packsEnabled) {
      this.getPacks();
    }

    if (selectedFilter.value === 'Services') {
      this.getServices();
    }
  }

  submitNoStockFoundForMaterial(stock: Stock) {
    if (!this.enableTrackingForNoStockItems) {
      return;
    }

    var materialWithNoStock = new NoStockMaterialItem();
    materialWithNoStock.stockLocationId =
      this.deviceService.getStockLocationId();
    materialWithNoStock.institutionId = this.deviceService.getInstitutionId();
    materialWithNoStock.materialBarcode = stock.barcode;
    materialWithNoStock.deviceId = this.deviceService.getDeviceId();
    materialWithNoStock.materialId = stock.materialId;
    materialWithNoStock.patientId = this.patientId;
    materialWithNoStock.userName = "Unknown";

    this.stockService
      .submitNoStockForMaterial(materialWithNoStock)
      .subscribe(() => { });
  }

  onSearchHeaderInputFocused(value: boolean) {
    this.searchHeaderInputFocused.emit(value);
  }
}
